import type { StatsigOptions } from "@statsig/react-bindings";

export type TapestryClientEventsServiceEnvironment = "development" | "staging" | "prod";
export type TapestryStatsigNetworkConfig = NonNullable<StatsigOptions["networkConfig"]>;

const FALLBACK_BASE_ORIGIN = "http://localhost";

export const CLIENT_EVENTS_SERVICE_DOMAINS = {
  development: "localhost:7009",
  staging: "chatgpt-staging.com/ces",
  prod: "chatgpt.com/ces",
} as const satisfies Record<TapestryClientEventsServiceEnvironment, string>;

export function getClientEventsServiceDomain(env: TapestryClientEventsServiceEnvironment): string {
  return CLIENT_EVENTS_SERVICE_DOMAINS[env];
}

export function getClientEventsServiceProtocol(env: string | undefined): "http" | "https" {
  return env === "development" ? "http" : "https";
}

export function getClientEventsServiceLogEventUrl(
  domain: string | undefined,
  env: string | undefined,
): string | undefined {
  if (!domain) {
    return undefined;
  }
  return `${getClientEventsServiceProtocol(env)}://${domain}/v1/rgstr`;
}

export function getClientEventsServiceStatscFlushUrl(
  domain: string | undefined,
  env: string | undefined,
): string | undefined {
  if (!domain) {
    return undefined;
  }
  return `${getClientEventsServiceProtocol(env)}://${domain}/statsc/flush`;
}

export function getClientEventsServiceStatsigNetworkConfig(
  domain: string | undefined,
  env: string | undefined,
): TapestryStatsigNetworkConfig | undefined {
  const logEventUrl = getClientEventsServiceLogEventUrl(domain, env);
  if (!logEventUrl) {
    return undefined;
  }
  return {
    logEventUrl,
    networkOverrideFunc: (url, args) => {
      let isLogEventUrl = false;
      try {
        const requestUrl = new URL(url, FALLBACK_BASE_ORIGIN);
        const expectedUrl = new URL(logEventUrl, FALLBACK_BASE_ORIGIN);
        const requestPathname = requestUrl.pathname.replace(/\/+$/, "") || "/";
        const expectedPathname = expectedUrl.pathname.replace(/\/+$/, "") || "/";
        isLogEventUrl =
          requestUrl.protocol === expectedUrl.protocol &&
          requestUrl.host === expectedUrl.host &&
          requestPathname === expectedPathname;
      } catch {
        isLogEventUrl = false;
      }

      return fetch(url, isLogEventUrl ? { ...args, credentials: "include" as const } : args);
    },
  };
}
